import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, DollarSign, Shield, Ticket, Search, ChevronLeft, ChevronRight, UserCheck, UserX, Crown, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';
import BoxSeatLogo from '../components/BoxSeatLogo';

const StatCard = ({ icon: Icon, label, value, accent = false }) => (
  <div className="bg-surface border border-white/10 rounded-xl p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent ? 'bg-primary/15 text-primary' : 'bg-white/5 text-zinc-400'}`}>
        <Icon size={18} />
      </div>
      <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">{label}</span>
    </div>
    <p className={`font-heading text-3xl font-black ${accent ? 'text-primary' : 'text-white'}`}>{value}</p>
  </div>
);

const SUB_LABELS = {
  active: { text: 'Paid', color: 'bg-primary/20 text-primary' },
  promo: { text: 'Promo', color: 'bg-secondary/20 text-secondary' },
  none: { text: 'None', color: 'bg-white/5 text-zinc-500' },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) navigate('/dashboard');
    }
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.append('search', search);
      if (subFilter) params.append('subscription', subFilter);
      const res = await axios.get(`${API}/admin/users?${params}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      if (err.response?.status === 403) navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [page, search, subFilter, navigate]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleAction = async (userId, update) => {
    setActionLoading(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}`, update);
      await fetchUsers();
      await fetchStats();
    } catch (err) {
      alert(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-bg text-white">
      {/* Header */}
      <header data-testid="admin-header" className="sticky top-0 z-50 backdrop-blur-2xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <BoxSeatLogo size="xs" onClick={() => navigate('/')} />
            <span className="hidden sm:inline text-xs font-bold uppercase tracking-wider text-zinc-500 bg-white/5 px-2.5 py-1 rounded">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-sm text-zinc-400 hover:text-white transition-colors flex items-center gap-1">
              <ArrowLeft size={14} /> Dashboard
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="text-sm text-zinc-400 hover:text-white transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight mb-8">Members Dashboard</h1>

        {/* Stats */}
        {stats && (
          <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Total Members" value={stats.total_users} accent />
            <StatCard icon={DollarSign} label="Paid Subscribers" value={stats.paid_subscribers} />
            <StatCard icon={Ticket} label="Promo Users" value={stats.promo_subscribers} />
            <StatCard icon={DollarSign} label="Revenue" value={`$${stats.total_revenue.toFixed(2)}`} accent />
          </div>
        )}

        {/* Promo Code Info */}
        {stats?.promo_code && (
          <div className="bg-surface border border-white/10 rounded-xl p-5 mb-8 flex flex-wrap items-center gap-6">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Promo Code</span>
              <p className="font-heading text-lg font-bold text-primary mt-1">{stats.promo_code.code}</p>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Uses</span>
              <p className="font-heading text-lg font-bold text-white mt-1">{stats.promo_code.current_uses} / {stats.promo_code.max_uses}</p>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="w-full bg-white/5 rounded-full h-2.5">
                <div className="bg-primary rounded-full h-2.5 transition-all" style={{ width: `${(stats.promo_code.current_uses / stats.promo_code.max_uses) * 100}%` }} />
              </div>
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="admin-search-input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2.5 bg-surface border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <button type="submit" className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm font-bold uppercase tracking-wide text-zinc-300 hover:bg-white/10 transition-all">Search</button>
          </form>
          <select
            data-testid="admin-sub-filter"
            value={subFilter}
            onChange={(e) => { setSubFilter(e.target.value); setPage(1); }}
            className="px-4 py-2.5 bg-surface border border-white/10 rounded-lg text-sm text-white focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
          >
            <option value="">All Subscriptions</option>
            <option value="active">Paid</option>
            <option value="promo">Promo</option>
            <option value="none">None</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="bg-surface border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="admin-users-table" className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Member</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Subscription</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Status</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Joined</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="px-5 py-12 text-center">
                    <Loader2 size={24} className="text-primary animate-spin mx-auto" />
                  </td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan="5" className="px-5 py-12 text-center text-zinc-500">No members found</td></tr>
                ) : users.map(u => {
                  const sub = SUB_LABELS[u.subscription_status] || SUB_LABELS.none;
                  const isCurrentUser = u.id === user?.id;
                  return (
                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold uppercase">
                            {(u.full_name || u.email)[0]}
                          </div>
                          <div>
                            <p className="font-semibold text-white flex items-center gap-2">
                              {u.full_name || '—'}
                              {u.is_admin && <Crown size={13} className="text-primary" />}
                              {isCurrentUser && <span className="text-[10px] text-zinc-500">(you)</span>}
                            </p>
                            <p className="text-xs text-zinc-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-block px-2.5 py-0.5 rounded text-xs font-bold uppercase ${sub.color}`}>{sub.text}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${u.is_active ? 'bg-secondary' : 'bg-accent'}`} />
                        <span className="text-xs text-zinc-400">{u.is_active ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td className="px-5 py-3.5 text-zinc-400 text-xs">{formatDate(u.created_at)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {actionLoading === u.id ? (
                            <Loader2 size={14} className="text-primary animate-spin" />
                          ) : (
                            <>
                              {/* Grant/Revoke Subscription */}
                              {u.subscription_status === 'none' ? (
                                <button
                                  data-testid={`grant-sub-${u.id}`}
                                  onClick={() => handleAction(u.id, { subscription_status: 'active' })}
                                  title="Grant Season Pass"
                                  className="w-7 h-7 rounded bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors"
                                >
                                  <UserCheck size={13} />
                                </button>
                              ) : (
                                <button
                                  data-testid={`revoke-sub-${u.id}`}
                                  onClick={() => handleAction(u.id, { subscription_status: 'none' })}
                                  title="Revoke Season Pass"
                                  className="w-7 h-7 rounded bg-accent/10 text-accent flex items-center justify-center hover:bg-accent/20 transition-colors"
                                >
                                  <UserX size={13} />
                                </button>
                              )}
                              {/* Toggle Admin */}
                              {!isCurrentUser && (
                                <button
                                  data-testid={`toggle-admin-${u.id}`}
                                  onClick={() => handleAction(u.id, { is_admin: !u.is_admin })}
                                  title={u.is_admin ? 'Remove Admin' : 'Make Admin'}
                                  className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${u.is_admin ? 'bg-primary/20 text-primary hover:bg-primary/30' : 'bg-white/5 text-zinc-500 hover:bg-white/10'}`}
                                >
                                  <Crown size={13} />
                                </button>
                              )}
                              {/* Deactivate/Activate */}
                              {!isCurrentUser && (
                                <button
                                  data-testid={`toggle-active-${u.id}`}
                                  onClick={() => handleAction(u.id, { is_active: !u.is_active })}
                                  title={u.is_active ? 'Deactivate User' : 'Activate User'}
                                  className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${u.is_active ? 'bg-white/5 text-zinc-500 hover:bg-accent/10 hover:text-accent' : 'bg-secondary/10 text-secondary hover:bg-secondary/20'}`}
                                >
                                  <Shield size={13} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-white/10">
              <span className="text-xs text-zinc-500">{total} members total</span>
              <div className="flex items-center gap-2">
                <button
                  data-testid="admin-prev-page"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="w-8 h-8 rounded bg-white/5 flex items-center justify-center text-zinc-400 hover:bg-white/10 disabled:opacity-30 transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-xs text-zinc-400 min-w-[60px] text-center">{page} / {totalPages}</span>
                <button
                  data-testid="admin-next-page"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="w-8 h-8 rounded bg-white/5 flex items-center justify-center text-zinc-400 hover:bg-white/10 disabled:opacity-30 transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
